import {
  CardFormArgs,
  CardFormTokenResponse,
  CardFormVariant,
  CoinflowCardForm,
  CoinflowEnvs,
  MerchantStyle,
  MerchantTheme
} from '@coinflowlabs/angular';

export const COINFLOW_TOKENIZE_TIMEOUT_MS = 30_000;

/**
 * A card the hosted form refused to tokenize. Carries `isValid: false` so
 * coinflowErrorMessage reads it like the old TokenEx validity flags.
 */
export class CardFormTokenizeError extends Error {
  readonly isValid = false;
}

/**
 * One source for the Coinflow-hosted card form theme; only the accent differs
 * per flow. Accent strings stay at the call sites — they are flow theme
 * decisions (gaming '#7332f5', merch '#94ff2a') and exporting constants here
 * would invite cross-flow reuse of the wrong one.
 */
export function buildCoinflowCardFormTheme(primary: string): MerchantTheme {
  return {
    primary,
    background: '#27232B',
    textColor: '#FFFFFF',
    fontSize: '13px',
    style: MerchantStyle.Rounded,
    showCardIcon: true
  };
}

export function coinflowCardFormArgs(opts: {
  merchantId: string;
  env: string;
  variant: CardFormVariant;
  accent: string;
  token?: string;
}): CardFormArgs {
  return {
    merchantId: opts.merchantId,
    env: opts.env as CoinflowEnvs,
    variant: opts.variant,
    theme: buildCoinflowCardFormTheme(opts.accent),
    ...(opts.token ? { token: opts.token } : {})
  };
}

/** The SDK's tokenize() never settles when the iframe stops answering. */
export async function tokenizeCardForm(
  cardForm: CoinflowCardForm,
  timeoutMs = COINFLOW_TOKENIZE_TIMEOUT_MS
): Promise<CardFormTokenResponse> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Coinflow card form did not respond to tokenize')), timeoutMs);
  });
  const tokenized = cardForm.tokenize().catch((err: unknown) => {
    throw new CardFormTokenizeError(err instanceof Error ? err.message : String(err), { cause: err });
  });

  try {
    return await Promise.race([tokenized, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The hosted form also sends the card's last four and brand, which the SDK's
 * types leave out; they stay optional so a payload without them still pays.
 */
export interface NewCardToken extends Required<CardFormTokenResponse> {
  lastFour?: string;
  cardType?: string;
}

/** A new card's checkout needs the expiry the card-form variant collects. */
export async function tokenizeNewCard(cardForm: CoinflowCardForm, timeoutMs?: number): Promise<NewCardToken> {
  const { token, expMonth, expYear, lastFour, cardType } = (await tokenizeCardForm(cardForm, timeoutMs)) as Partial<
    NewCardToken
  > &
    CardFormTokenResponse;
  if (!expMonth || !expYear) {
    throw new Error('Coinflow card form returned no expiry');
  }
  return {
    token,
    expMonth,
    expYear,
    ...(typeof lastFour === 'string' ? { lastFour } : {}),
    ...(typeof cardType === 'string' ? { cardType } : {})
  };
}
